// ============================================================
// TMR Portal Admin — creating and editing custom roles (10 B7b)
// ============================================================
import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { TextField, SelectField } from '../../components/fields';
import { Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { permissionStanding } from '../../data/authorization/roles';
import { useCreateRole, useFeatureCatalog, useUpdateRole } from '../../data/authorization/queries';
import type { AuthorizationRole, CatalogFeature } from '../../data/authorization/contract';
import { ErrorBanner } from './ErrorBanner';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link RoleFormModal}. */
interface RoleFormModalProps {
  /** The role being edited, or null to create one. */
  role: AuthorizationRole | null;
  /** Every role, for the parent picker and for resolving what a parent already grants. */
  roles: AuthorizationRole[];
  onClose: () => void;
}

/**
 * Creates or edits a custom role: its name, the role it inherits from, and the permissions it grants.
 *
 * The permission list it submits is **wholesale** — a key left unchecked is revoked, not kept — so the
 * form always sends the full direct set it is showing, and nothing else.
 *
 * A permission can be both direct and inherited, and the two are not interchangeable: the direct grant
 * survives the parent changing, the inherited one does not. So the form never drops a direct grant just
 * because the parent happens to cover it — doing that would let a save that only fixed a typo silently
 * destroy a grant, with the damage surfacing much later, when someone re-parents the role.
 *
 * Anti-escalation (06 §5.1) is the server's call, not this form's: it can only compare against what the
 * session was told at load, while the backend resolves the operator's permissions fresh. A 403 here is
 * shown, never pre-empted.
 */
export function RoleFormModal({ role, roles, onClose }: RoleFormModalProps) {
  const catalog = useFeatureCatalog();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole(role?.id ?? '');
  const save = role ? updateRole : createRole;

  const [name, setName] = useState(role?.name ?? '');
  const [parentRoleId, setParentRoleId] = useState(role?.parentRoleId ?? '');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set(role?.directPermissions ?? []));
  const [touched, setTouched] = useState(false);

  // What the chosen parent already grants — inherited, so not part of this role's direct set.
  const inherited = useMemo(() => {
    const parent = roles.find((candidate) => candidate.id === parentRoleId);
    return new Set(parent?.effectivePermissions ?? []);
  }, [roles, parentRoleId]);

  // A role cannot inherit from itself. Deeper cycles are the server's guard (RoleHierarchy), which sees
  // the whole graph — including branches this list may not show.
  const parentOptions = roles
    .filter((candidate) => candidate.id !== role?.id)
    .map((candidate) => ({ value: candidate.id, label: candidate.name }));

  const nameError = name.trim().length < 2 ? 'Informe o nome do papel' : null;

  // Saving writes the permission set wholesale, so the form must not submit a set the operator was never
  // shown. With the catalogue down, changing "Herda de" and saving would drop every permission the role
  // received through the old parent — with no checkbox on screen to hint at what was being lost.
  const catalogReady = catalog.isSuccess;

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submit = () => {
    if (nameError || !catalogReady) {
      setTouched(true);
      return;
    }

    save.mutate(
      {
        name: name.trim(),
        parentRoleId: parentRoleId || null,
        permissionKeys: [...selected],
      },
      { onSuccess: onClose },
    );
  };

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 620, maxWidth: '100%', maxHeight: '90vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div className="pa-eyebrow" style={{ marginBottom: 4 }}>{role ? 'Editar papel' : 'Novo papel'}</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>
              {role ? role.name : 'Criar papel customizado'}
            </h3>
          </div>

          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
            <ErrorBanner error={save.error} detailFor={nameConflictDetail} />

            <div className="pa-grid-2">
              <TextField
                label="Nome"
                required
                value={name}
                onChange={setName}
                error={touched && nameError}
                placeholder="Ex.: Operador de cobrança"
              />
              <SelectField
                label="Herda de"
                value={parentRoleId}
                onChange={setParentRoleId}
                options={parentOptions}
                placeholder="Nenhum papel"
              />
            </div>

            <div>
              <label className="pa-form-label">Permissões</label>
              {catalog.isPending ? (
                <Skeleton rows={3} />
              ) : catalog.isError ? (
                <>
                  <ErrorBanner error={catalog.error} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <Btn variant="secondary" size="sm" onClick={() => void catalog.refetch()}>Tentar de novo</Btn>
                    <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Não é possível salvar sem carregar as permissões.
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {catalog.data.map((feature) => (
                    <FeatureGroup
                      key={feature.id}
                      feature={feature}
                      selected={selected}
                      inherited={inherited}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-100)', fontSize: 12.5, color: 'var(--fg-muted)' }}>
              <StrokeIcon d={Icons.shield} size={14} style={{ flexShrink: 0, color: 'var(--stone-400)' }} />
              Um papel só pode conceder permissões que você mesmo possui.
            </div>
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose}>Cancelar</Btn>
            <Btn variant="primary" icon={Icons.check} onClick={submit} disabled={save.isPending || !catalogReady}>
              {save.isPending ? 'Salvando…' : role ? 'Salvar alterações' : 'Criar papel'}
            </Btn>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

/** The name is the only unique field on a role, so a 409 here can only be about it. */
function nameConflictDetail(error: ApiError): string | null {
  return error.isConflict ? 'Já existe um papel com este nome.' : null;
}

/** Props for {@link FeatureGroup}. */
interface FeatureGroupProps {
  feature: CatalogFeature;
  selected: ReadonlySet<string>;
  inherited: ReadonlySet<string>;
  onToggle: (key: string) => void;
}

/** The permissions of one Feature — the grouping the backend already publishes (10 B7a). */
function FeatureGroup({ feature, selected, inherited, onToggle }: FeatureGroupProps) {
  return (
    <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', margin: 0 }}>
      <legend style={{ padding: '0 6px', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>
        {feature.name}
      </legend>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {feature.permissions.map((permission) => {
          const standing = permissionStanding(permission.key, selected, inherited);
          return (
            <label
              key={permission.id}
              style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5, color: standing.locked ? 'var(--fg-muted)' : 'var(--ink-900)', cursor: standing.locked ? 'default' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={standing.checked}
                disabled={standing.locked}
                onChange={() => onToggle(permission.key)}
              />
              <span>{permission.name}</span>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-subtle)' }}>{permission.key}</code>
              {standing.inherited && (
                <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)', fontStyle: 'italic' }}>
                  {standing.direct ? 'direta e herdada' : 'herdada'}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
